import { useEffect, useId } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLatest, useUpdateEffect } from 'react-use';
import { yupResolver } from '@hookform/resolvers/yup';
import { DialogContent, Stack } from '@mui/material';

import type { Drug } from '@/api/domains/drugs.types';
import { useGlobalErrorStore } from '@/components/dialogs';
import { Form, RHFGallery, type RHFGalleryProps } from '@/components/form-controls';
import { useGalleryStoreActions } from '@/components/Gallery/gallery.store';
import type { GalleryImageInfo } from '@/components/Gallery/types';
import { LoadingIndicator } from '@/components/LoadingIndicator';
import { ModalBody } from '@/components/Modal/ModalBody2';
import { IMAGE_FILE_TYPES } from '@/shared/constants/file-type.constants';
import { FILE_LIMIT_ERROR, FILE_SIZE_ERROR, FILE_TYPE_ERROR, MAX_FILE_SIZE } from '@/shared/constants/images.constants';
import { Yup } from '@/shared/lib/yup';

import { NomenclatureConfirmationEditDialog } from '../../dialogs';
import { nomenclatureCreateEvent } from '../../events';
import {
  useNomenclatureContextWindows,
  useNomenclatureStoreActions,
  useNomenclatureStorePreferredTab,
} from '../../useNomenclatureStore';
import { NomenclatureModalSidebar } from '../NomenclatureModalSidebar';
import type { NomenclatureModalTab } from '../NomenclatureModalTabbar';
import type { NomenclatureModalVariant } from '../types';
import { convertDrugToGalleryImages, convertGalleryToDrugImages } from './helpers';
import { ImageFileField } from './ImageFileField';
import { useDrugImages } from './useDrugImages';
import { useUpdateImages } from './useUpdateImages';

type FormValues = Yup.InferType<typeof formSchema>;

const CURRENT_TAB_NAME: NomenclatureModalTab = 'images';

const formSchema = Yup.object().shape({
  file: Yup.mixed<File>()
    .nullable()
    .fileValidation({
      correctTypes: IMAGE_FILE_TYPES,
      maxSize: MAX_FILE_SIZE,
      typeErrorMessage: FILE_TYPE_ERROR,
      sizeErrorMessage: FILE_SIZE_ERROR,
    })
    .test('file_limit', FILE_LIMIT_ERROR, (value, context) => {
      if (!value) return true;
      const {
        parent: { images },
      } = context;

      if (images && images.length >= 5) {
        return false;
      }

      return true;
    }),
  images: Yup.array().of(Yup.mixed<GalleryImageInfo>().required()),
});

type ImagesFormProps = {
  variant: NomenclatureModalVariant;
  drug: Drug;
  onClose: VoidFunction;
};

export const ImagesForm = (props: ImagesFormProps) => {
  const { variant, drug, onClose } = props;

  const preferredTab = useNomenclatureStorePreferredTab();
  const isEditMode = variant === 'edit';
  const isEditAfterCreationMode = variant === 'editAfterCreation';

  const methods = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(formSchema),
    defaultValues: {
      file: null,
      images: [],
    },
  });

  const {
    watch,
    formState: { isSubmitting, isDirty },
    handleSubmit: wrapHandleSubmit,
    trigger,
    reset,
  } = methods;

  const imageFieldFile = watch('file');

  const { closeConfirmationEditDialog, openConfirmationEditDialog, setActiveTab, setPreferredTab } =
    useNomenclatureStoreActions();
  const { openEditDialog } = useGalleryStoreActions();
  const { isConfirmationEditDialogVisible } = useNomenclatureContextWindows();
  const { data: storedImages, isLoading: isDrugImagesLoading } = useDrugImages({ drug_id: drug.id });
  const { mutateAsync: updateImages, isLoading: isUpdateImagesLoading } = useUpdateImages();
  const { setGlobalError } = useGlobalErrorStore();

  const latestFormState = useLatest({ isDirty });
  useUpdateEffect(() => {
    if (preferredTab === CURRENT_TAB_NAME) {
      return;
    }

    const { isDirty } = latestFormState.current;
    if (isDirty) {
      openConfirmationEditDialog();
    } else {
      setActiveTab(preferredTab);
    }
  }, [preferredTab]);

  useEffect(() => {
    if (storedImages) {
      reset({
        file: imageFieldFile,
        images: convertDrugToGalleryImages(storedImages),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedImages]);

  const submitHandler = async ({ images }: FormValues) => {
    if (!images) return;

    try {
      await updateImages({
        drug_id: drug.id,
        images: convertGalleryToDrugImages(images),
      });

      if (isEditAfterCreationMode) {
        if (drug) {
          nomenclatureCreateEvent.emit(drug);
        }
      }

      if (isConfirmationEditDialogVisible) {
        closeConfirmationEditDialog();
      }

      if (preferredTab !== CURRENT_TAB_NAME) {
        setActiveTab(preferredTab);
      }

      if (preferredTab === CURRENT_TAB_NAME) {
        onClose();
      }
    } catch (error) {
      setGlobalError(error);
      setPreferredTab(CURRENT_TAB_NAME);
    }
  };

  const handleNomenclatureConfirmationEditDialogClose = () => {
    if (preferredTab === CURRENT_TAB_NAME) {
      onClose();
    } else {
      setActiveTab(preferredTab);
    }
  };

  useUpdateEffect(() => {
    const validateAndOpenDialog = async (imageFile: File) => {
      const isValid = await trigger('file');

      if (isValid) {
        openEditDialog(imageFile);
      }
    };

    if (imageFieldFile) {
      validateAndOpenDialog(imageFieldFile);
    }
  }, [imageFieldFile]);

  const handleImageRemove: RHFGalleryProps['onImageRemove'] = async images => {
    await updateImages({
      drug_id: drug.id,
      images: convertGalleryToDrugImages(images),
    });
  };

  const handleImagesEdit: RHFGalleryProps['onImagesEdit'] = async images => {
    await updateImages({
      drug_id: drug.id,
      images: convertGalleryToDrugImages(images),
    });
  };

  const formId = useId();

  return (
    <FormProvider {...methods}>
      <Stack direction="row" spacing={1} flexGrow={1}>
        <ModalBody
          slotProps={{
            root: {
              sx: { borderRadius: 0 },
            },
            content: {
              sx: {
                alignItems: 'center',
                borderRight: theme => `1px solid ${theme.palette.divider}`,
              },
            },
          }}
        >
          <DialogContent sx={{ pt: 3, width: 768 }}>
            <Form
              sx={{ flexGrow: 1 }}
              id={formId}
              isDisabled={isDrugImagesLoading}
              onSubmit={wrapHandleSubmit(submitHandler)}
            >
              <Stack spacing={2}>
                <ImageFileField />

                {isDrugImagesLoading && <LoadingIndicator slotProps={{ container: { sx: { minHeight: 330 } } }} />}
                <RHFGallery
                  name="images"
                  onImagesEdit={handleImagesEdit}
                  onImageRemove={handleImageRemove}
                  isLoading={isUpdateImagesLoading}
                  imagePrefix="drugs"
                />
              </Stack>
            </Form>
          </DialogContent>
        </ModalBody>

        <NomenclatureModalSidebar
          drug={drug}
          isEditMode={isEditMode || isEditAfterCreationMode}
          formId={formId}
          onCancel={onClose}
        />
      </Stack>

      <NomenclatureConfirmationEditDialog
        isLoading={isSubmitting}
        onProceed={wrapHandleSubmit(submitHandler)}
        onClose={handleNomenclatureConfirmationEditDialogClose}
      />
    </FormProvider>
  );
};
